import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
} from '@mui/material';
import { Add as AddIcon, Edit as EditIcon } from '@mui/icons-material';
import Input from '../../../components/Input';
import TextArea from '../../../components/TextArea';

function AddVisitTypeCategoryModal({ open, onClose, onConfirm, visitTypeCategoryData, editMode = false }) {
  const { t } = useTranslation();

  const [formData, setFormData] = useState({
    name: '',
    description: '',
  });

  const [errors, setErrors] = useState({});

  // Initialize form data when dialog opens or when visitTypeCategoryData changes
  useEffect(() => {
    if (open) {
      if (editMode && visitTypeCategoryData) {
        setFormData({
          id: visitTypeCategoryData.id,
          name: visitTypeCategoryData.name || '',
          description: visitTypeCategoryData.description || '',
        });
      } else {
        setFormData({
          name: '',
          description: '',
        });
      }
      setErrors({});
    }
  }, [open, visitTypeCategoryData, editMode]);

  // Handle input change
  const handleChange = (field, value) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));

    // Validate field in real-time
    const newErrors = { ...errors };

    if (field === 'name') {
      if (!value.trim()) {
        newErrors.name = t('visitTypeCategories.addDialog.nameRequired');
      } else if (value.length > 100) {
        newErrors.name = t('visitTypeCategories.addDialog.nameMaxLength');
      } else {
        delete newErrors.name;
      }
    } else if (field === 'description') {
      if (value && value.length > 255) {
        newErrors.description = t('visitTypeCategories.addDialog.descriptionMaxLength');
      } else {
        delete newErrors.description;
      }
    }

    setErrors(newErrors);
  };

  // Check if all required fields are filled
  const isFormValid = () => {
    return formData.name.trim() !== '';
  };

  // Validate form
  const validateForm = () => {
    const newErrors = {};

    if (!formData.name.trim()) {
      newErrors.name = t('visitTypeCategories.addDialog.nameRequired');
    } else if (formData.name.length > 100) {
      newErrors.name = t('visitTypeCategories.addDialog.nameMaxLength');
    }

    if (formData.description && formData.description.length > 255) {
      newErrors.description = t('visitTypeCategories.addDialog.descriptionMaxLength');
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Handle confirm
  const handleConfirm = () => {
    if (validateForm()) {
      onConfirm(formData);
    }
  };

  // Handle cancel
  const handleCancel = () => {
    onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
    >
      <DialogTitle
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          color: editMode ? 'primary.main' : 'success.main',
        }}
      >
        {editMode ? <EditIcon /> : <AddIcon />}
        {editMode ? t('visitTypeCategories.editDialog.title') : t('visitTypeCategories.addDialog.title')}
      </DialogTitle>
      <DialogContent>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5, pt: 2 }}>
          {/* Name */}
          <Input
            label={t('visitTypeCategories.addDialog.name')}
            value={formData.name}
            onChange={(e) => handleChange('name', e.target.value)}
            required
            error={!!errors.name}
            helperText={errors.name}
            placeholder={t('visitTypeCategories.addDialog.name')}
            id="name"
            name="name"
            data-testid="visitTypeCategory-modal-input-name"
          />

          {/* Description */}
          <TextArea
            label={t('visitTypeCategories.addDialog.description')}
            value={formData.description}
            onChange={(e) => handleChange('description', e.target.value)}
            rows={3}
            placeholder={t('visitTypeCategories.addDialog.description')}
            error={!!errors.description}
            helperText={errors.description}
            id="description"
            name="description"
            data-testid="visitTypeCategory-modal-input-description"
          />
        </Box>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button
          onClick={handleCancel}
          variant="outlined"
          id="cancel"
          name="cancel"
          data-testid="visitTypeCategory-modal-button-cancel"
        >
          {t('visitTypeCategories.addDialog.cancel')}
        </Button>
        <Button
          onClick={handleConfirm}
          variant="contained"
          color="primary"
          disabled={!isFormValid() || Object.values(errors).some(error => error !== '')}
          startIcon={editMode ? <EditIcon /> : <AddIcon />}
          id="confirm"
          name="confirm"
          data-testid="visitTypeCategory-modal-button-confirm"
        >
          {editMode ? t('visitTypeCategories.editDialog.confirm') : t('visitTypeCategories.addDialog.confirm')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default AddVisitTypeCategoryModal;

